/**
 * resolveVictoryPileCardPick move — resolves a pending victory-pile villain-pick
 * player choice (WP-285 / D-24067).
 *
 * Called by the active player after a victory-villain-attack hero ability parked a
 * PendingVictoryPileCardPick on G.pendingVictoryPileCardPick (FIFO). The player
 * picks one villain from their victory pile by cardId; the move grants attack equal
 * to that villain's printed fight cost from G.cardStats[cardId].fightCost.
 * Note: villain cards store the "printed [icon:attack]" as fightCost (the fight
 * requirement). The .attack field is always 0 for villain cards.
 *
 * Resolution-time eligibility is authoritative — the victory pile may have changed
 * between park time and resolve time. The move re-filters the victory pile at
 * resolve time; a stale or absent target is a silent no-op (queue intact, no attack
 * granted, player resubmits with a valid target).
 *
 * Atomicity: attack is granted ONLY after the cardId passes all eligibility checks.
 * No partial mutation on any failure path.
 *
 * No registry imports. No .reduce(). Moves never throw.
 */
import { formatCardRef } from '../log/logDisplay.js';
import { pushLog } from '../log/logPush.js';
/**
 * Whether any victory-pile villain-pick is currently pending.
 *
 * Single predicate imported by the block-all action-move guards and the
 * getLegalMoves short-circuit. `undefined` and `[]` both mean no pending pick
 * (mirrors hasPendingOptionalKoReward, D-24007).
 *
 * // why: pendingVictoryPileCardPick is lazy-init (D-24067); undefined and [] both mean no pending pick
 *
 * @param G - The game state to inspect (not mutated).
 * @returns true when the pending victory-pile pick queue holds at least one entry.
 */
export function hasPendingVictoryPileCardPick(G) {
    return (G.pendingVictoryPileCardPick?.length ?? 0) > 0;
}
/**
 * Returns the villain CardExtIds currently eligible for a victory-pile pick.
 *
 * Eligibility: card must be present in G.playerZones[playerID].victory AND
 * G.villainDeckCardTypes[cardId] must equal 'villain' or 'henchman' (not
 * bystander, scheme-twist, or mastermind-strike; tactics never appear in the map).
 *
 * // why: filter reads setup-time villain-deck type map (WP-014B); tactics are in G.mastermind, never in this map, so the villain/henchman check cleanly excludes them. Henchmen are included per rules v23 "Henchmen Are Villains" (D-24608)
 *
 * Called at resolve time (not park time) so the eligibility list is always
 * authoritative — victory pile contents may have changed since the pick was parked.
 *
 * @param G - The game state to read (not mutated).
 * @param playerID - The player whose victory pile to inspect.
 * @returns Array of eligible villain CardExtIds, in victory pile order.
 */
export function getEligibleVictoryVillains(G, playerID) {
    const playerZones = G.playerZones[playerID];
    if (!playerZones) {
        return [];
    }
    const eligible = [];
    for (const cardId of playerZones.victory) {
        // why: filter reads setup-time villain-deck type map (WP-014B); tactics are in G.mastermind, never in this map, so they stay excluded.
        // Henchmen count: rules v23 "Henchmen Are Villains" — "Henchman Villain cards are indeed Villains" (D-24608; same correction as D-24603).
        const cardType = G.villainDeckCardTypes[cardId];
        // why: a defeated Killbot is typed 'villain' for routing but counts as a Bystander
        // in the Victory Pile, not a Villain (rules v23 L2341), so it is never eligible.
        if (G.convertedVillainOrigins?.[cardId] === 'killbot') {
            continue;
        }
        if (cardType === 'villain' || cardType === 'henchman') {
            eligible.push(cardId);
        }
    }
    return eligible;
}
/**
 * Resolves the FRONT pending victory-pile villain-pick choice.
 *
 * Atomic sequence (HARD — exact order, mirrors optionalKoReward.resolve.ts):
 *   1. Validate args — cardId must be a non-empty string; invalid → silent no-op.
 *   2. Validate the front pending entry — non-empty queue, front.playerID match.
 *   3. Re-filter eligible villains from G at resolve time (victory pile may have
 *      changed since park time). Empty eligible list → silent no-op, queue intact.
 *   4. Confirm cardId is in the eligible list. Absent/stale → silent no-op, queue
 *      intact (player resubmits with a valid target).
 *   5. Read villain fight cost from G.cardStats[cardId].fightCost (villain's printed attack value).
 *   6. Grant attack → G.turnEconomy.attack += attack.
 *   7. Front-pop (queue.shift()) LAST, mirroring WP-248.
 *
 * Any failure before step 6 ABORTS the attack grant (no partial mutation).
 * Moves never throw.
 *
 * // why: block-all guard — no other move may fire while a pending victory-pile pick is outstanding (D-24067)
 *
 * @param context - boardgame.io move context with G and playerID.
 * @param args - the cardId to claim attack from.
 */
export function resolveVictoryPileCardPick({ G, playerID }, args) {
    // why: block-all guard — no other move may fire while a pending victory-pile pick is outstanding (D-24067)
    // Step 1: Validate args — cardId must be a non-empty string.
    // why: a client may submit the move with no payload (undefined) or null; reading a field off
    // either throws a TypeError, and moves never throw — reject before any field read.
    if (args === null || typeof args !== 'object') {
        return;
    }
    const cardId = args.cardId;
    if (typeof cardId !== 'string' || cardId.length === 0) {
        return;
    }
    const typedCardId = cardId;
    // Step 2: Validate the front pending entry.
    const queue = G.pendingVictoryPileCardPick;
    // why: pendingVictoryPileCardPick is lazy-init (D-24067); undefined and [] both mean no pending pick
    if (queue === undefined || queue.length === 0) {
        return;
    }
    const front = queue[0];
    if (front.playerID !== playerID) {
        return;
    }
    // Step 3: Re-filter at resolve time — the victory pile may have changed since park.
    const eligibleVillains = getEligibleVictoryVillains(G, playerID);
    if (eligibleVillains.length === 0) {
        return;
    }
    // Step 4: Confirm the chosen card is in the eligible list right now.
    let isEligible = false;
    for (const eligibleId of eligibleVillains) {
        if (eligibleId === typedCardId) {
            isEligible = true;
            break;
        }
    }
    if (!isEligible) {
        return;
    }
    // Step 5: Read the villain's printed fight cost from G.cardStats.
    // why: villain cards store the "printed [icon:attack]" as fightCost (the fight requirement),
    // not .attack (which is 0 for all villain cards — only heroes carry an .attack stat).
    // D-24067 specifies ".attack" but that field is always 0 for villains; fightCost is the
    // correct semantic match for the Ebony Blade's "printed Attack of a Villain" mechanic.
    if (!G.turnEconomy) {
        return;
    }
    const cardStat = G.cardStats[typedCardId];
    const attack = cardStat !== undefined ? cardStat.fightCost : 0;
    // Step 6: Grant attack — the ONLY G mutation on the success path.
    G.turnEconomy.attack += attack;
    pushLog(G, `Player ${playerID} claimed +${attack} attack from ${formatCardRef(G.cardDisplayData, typedCardId)} in their victory pile (Ebony Blade).`);
    // Step 7: Front-pop LAST (front-pop = Array.shift), mirroring WP-248.
    queue.shift();
}
//# sourceMappingURL=resolveVictoryPileCardPick.js.map